import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Lock, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 text-[#00FF94] animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl text-white font-semibold mb-2">Invalid Reset Link</h2>
            <p className="text-gray-400 mb-6">No reset token provided. Please request a new password reset.</p>
            <Link to="/forgot-password">
              <Button className="btn-primary">Request New Reset Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl text-white font-semibold mb-2">Link Expired or Invalid</h2>
            <p className="text-gray-400 mb-6">This password reset link has expired or is invalid. Please request a new one.</p>
            <Link to="/forgot-password">
              <Button className="btn-primary">Request New Reset Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
            <h2 className="text-xl text-white font-semibold mb-2">Password Reset Complete!</h2>
            <p className="text-gray-400 mb-6">Your password has been successfully reset. You can now login with your new password.</p>
            <Link to="/login">
              <Button className="btn-primary">Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md bg-[#0a0a0a] border-[#262626]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[#00FF94]/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#00FF94]" />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">Reset Your Password</CardTitle>
          <CardDescription className="text-gray-500">
            Enter a new password for <span className="text-white">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-10 pr-10 bg-[#171717] border-[#262626] text-white"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10 bg-[#171717] border-[#262626] text-white"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-xs text-gray-500 space-y-1">
              <p className={password.length >= 6 ? 'text-[#00FF94]' : ''}>
                {password.length >= 6 ? '✓' : '○'} At least 6 characters
              </p>
              <p className={password && password === confirmPassword ? 'text-[#00FF94]' : ''}>
                {password && password === confirmPassword ? '✓' : '○'} Passwords match
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={loading || password.length < 6 || password !== confirmPassword}
              className="w-full btn-primary"
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
